import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { useHistory, useLocation, useParams } from "react-router-dom";
import SupplierManagementShell from "../supplier/SupplierManagementShell";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import KpiPickerModal from "./KpiPickerModal";
import {
  cardSurface,
  cardBody,
  cardHeader,
  cardHeaderTitle,
  formLabel,
  primaryBtn,
  secondaryBtn,
  thStyle,
  tableCardSurface,
} from "./assessmentStyles";

/**
 * Single component for create AND edit. Edit mode is signalled by `:id` on the
 * route (/assessment/edit/:id). Company-owned drafts only reach here — the
 * View page gates that decision via its conditional "Edit" button.
 *
 * KPI composition supports three sources in one assessment and a depth=1
 * hierarchy (parent + optional sub-KPIs, no deeper). Local `kpis` state is
 * flat: each row optionally carries `parent_kpi_id`, `parent_kpi_id_source`
 * and, when the parent is RIU-backend, `parent_source_assessment_id`.
 * Rendering nests children under their parents; submit flattens.
 */
const CreateAssessment = () => {
  const history = useHistory();
  const location = useLocation();
  const { id: assessmentId } = useParams();
  const isEdit = Boolean(assessmentId);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [industry, setIndustry] = useState("");
  const [subIndustry, setSubIndustry] = useState("");
  const [status, setStatus] = useState("DRAFT");
  const [referencedAssessmentId, setReferencedAssessmentId] = useState(null);
  const [referencedAssessmentTitle, setReferencedAssessmentTitle] = useState("");
  const [kpis, setKpis] = useState([]);

  const [industries, setIndustries] = useState([]);
  const [subIndustriesByIndustry, setSubIndustriesByIndustry] = useState({});
  const [kpiCatalog, setKpiCatalog] = useState({});

  const [pickerOpen, setPickerOpen] = useState(false);
  const [subContext, setSubContext] = useState(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      const [iRes, kRes] = await Promise.all([
        AssessmentService.listIndustries(),
        AssessmentService.listKpis(),
      ]);
      const list = iRes?.data?.data ?? iRes?.data ?? [];
      if (Array.isArray(list)) {
        const grouped = {};
        const seen = list.map((row) => {
          grouped[row.industry_type_id] = (row.items ?? []).map((sub) => ({
            id: sub.id,
            name: sub.title,
          }));
          return { id: row.industry_type_id, name: row.industry_type_name };
        });
        setIndustries(seen);
        setSubIndustriesByIndustry(grouped);
      }
      const catalogList = kRes?.data?.data ?? [];
      if (Array.isArray(catalogList)) {
        const map = {};
        catalogList.forEach((k) => {
          map[k.id] = k.title;
        });
        setKpiCatalog(map);
      }
    })();
  }, []);

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      const res = await AssessmentService.getAssessment(assessmentId, "company");
      if (!res?.isSuccess) return;
      const a = res?.data?.data;
      if (!a) return;
      setTitle(a.title || "");
      setDescription(a.description || "");
      setIndustry(String(a.industry ?? ""));
      setSubIndustry(String(a.sub_industry ?? ""));
      setStatus(a.status_key || "DRAFT");
      const refId = a.referenced_assessment_id ?? null;
      setReferencedAssessmentId(refId);
      if (refId) {
        AssessmentService.getRiuTemplateKpis(refId).then((tplRes) => {
          if (tplRes?.isSuccess) {
            setReferencedAssessmentTitle(tplRes?.data?.data?.title || "");
          }
        });
      }
      setKpis(flattenFromServerTree(a.kpis || [], kpiCatalog));
    })();
  }, [assessmentId, isEdit, kpiCatalog]);

  const subIndustries = industry
    ? subIndustriesByIndustry[industry] || []
    : [];

  const topLevel = useMemo(() => kpis.filter((k) => k.parent_kpi_id == null), [kpis]);
  const childrenByParentKey = useMemo(() => {
    const map = new Map();
    for (const k of kpis) {
      if (k.parent_kpi_id == null) continue;
      const key = parentIdentityFromChild(k);
      const arr = map.get(key) ?? [];
      arr.push(k);
      map.set(key, arr);
    }
    return map;
  }, [kpis]);

  const toggleMandatory = (key) => {
    setKpis((prev) =>
      prev.map((k) => (kpiKey(k) === key ? { ...k, is_mandatory: !k.is_mandatory } : k))
    );
  };

  const setWeightage = (key, rawValue) => {
    const trimmed = String(rawValue ?? "").trim();
    setKpis((prev) =>
      prev.map((k) =>
        kpiKey(k) === key
          ? { ...k, weightage: trimmed === "" ? null : trimmed }
          : k
      )
    );
  };

  const removeKpi = (key) => {
    setKpis((prev) => {
      const target = prev.find((k) => kpiKey(k) === key);
      if (!target) return prev;
      if (target.parent_kpi_id == null) {
        const parentIdentity = selfIdentity(target);
        return prev.filter(
          (k) => kpiKey(k) !== key && parentIdentityFromChild(k) !== parentIdentity
        );
      }
      return prev.filter((k) => kpiKey(k) !== key);
    });
  };

  const openTopLevelPicker = () => {
    setSubContext(null);
    setPickerOpen(true);
  };

  const openSubPicker = (parentRow) => {
    setSubContext(parentRow);
    setPickerOpen(true);
  };

  const closePicker = () => {
    setPickerOpen(false);
    setSubContext(null);
  };

  const onPickerConfirm = (objs) => {
    if (subContext) {
      applySubPick(objs, subContext);
    } else {
      applyTopLevelPick(objs);
    }
    closePicker();
  };

  const applyTopLevelPick = (objs) => {
    setKpis((prev) => {
      const prevByKey = new Map(prev.map((k) => [kpiKey(k), k]));
      const objsKeys = new Set(
        objs.map((o) => `${o.source || "company"}:${o.id}`)
      );
      const kept = prev.filter((k) => objsKeys.has(kpiKey(k)));
      const additions = objs
        .filter((o) => !prevByKey.has(`${o.source || "company"}:${o.id}`))
        .map((o) => {
          const src = o.source || "company";
          return {
            id: o.id,
            title: o.title || kpiCatalog[o.id] || "",
            source: src,
            source_kpi_id: src === "riu_backend" ? o.source_kpi_id ?? o.id : undefined,
            source_assessment_id:
              src === "riu_backend" ? o.source_assessment_id ?? null : undefined,
            is_mandatory: false,
            weightage: null,
            default_weightage: o.default_weightage ?? null,
            parent_kpi_id: null,
            parent_kpi_id_source: null,
            parent_source_assessment_id: null,
          };
        });
      const combined = [...additions, ...kept];
      const combinedKeys = new Set(combined.map((k) => kpiKey(k)));
      return combined.filter((k) => {
        if (k.parent_kpi_id == null) return true;
        return combinedKeys.has(parentIdentityBaseKey(k));
      });
    });
  };

  const applySubPick = (objs, parent) => {
    setKpis((prev) => {
      const parentSelf = selfIdentity(parent);
      const existingChildrenKeys = new Set(
        prev
          .filter((k) => parentIdentityFromChild(k) === parentSelf)
          .map((k) => kpiKey(k))
      );
      const additions = [];
      for (const o of objs) {
        const src = o.source || "company";
        const identityKey = `${src}:${o.id}`;
        if (identityKey === kpiKey(parent)) continue;
        if (existingChildrenKeys.has(identityKey)) continue;
        if (prev.some((k) => kpiKey(k) === identityKey)) continue;
        additions.push({
          id: o.id,
          title: o.title || kpiCatalog[o.id] || "",
          source: src,
          source_kpi_id: src === "riu_backend" ? o.source_kpi_id ?? o.id : undefined,
          source_assessment_id:
            src === "riu_backend" ? o.source_assessment_id ?? null : undefined,
          is_mandatory: false,
          weightage: null,
          default_weightage: o.default_weightage ?? null,
          parent_kpi_id: parent.id,
          parent_kpi_id_source: parent.source || "company",
          parent_source_assessment_id:
            parent.source === "riu_backend"
              ? parent.source_assessment_id ?? null
              : null,
        });
      }
      return [...prev, ...additions];
    });
  };

  const pickerSelectedKeys = subContext
    ? (childrenByParentKey.get(selfIdentity(subContext)) || []).map((k) => ({
        id: k.id,
        source: k.source,
      }))
    : kpis.map((k) => ({ id: k.id, source: k.source }));

  const openTemplatePicker = async () => {
    setTemplatePickerOpen(true);
    setLoadingTemplates(true);
    const res = await AssessmentService.listRiuTemplates({
      status: "PUBLISHED",
      limit: 200,
    });
    const items = res?.data?.data?.items ?? res?.data?.data ?? [];
    setTemplates(Array.isArray(items) ? items : []);
    setLoadingTemplates(false);
  };

  const chooseTemplate = async (tpl) => {
    setTemplatePickerOpen(false);
    const res = await AssessmentService.getRiuTemplateKpis(tpl.id);
    if (!res?.isSuccess) return;
    const a = res?.data?.data;
    if (!a) return;
    setReferencedAssessmentId(tpl.id);
    setReferencedAssessmentTitle(a.title || tpl.title || "");
    if (!isEdit) {
      if (!title) setTitle(`Copy of ${a.title || tpl.title || ""}`.trim());
      if (!description && a.description) setDescription(a.description);
      if (!industry && a.industry) setIndustry(String(a.industry));
      if (!subIndustry && a.sub_industry) setSubIndustry(String(a.sub_industry));
    }
    setKpis(flattenFromServerTree(a.kpis || [], kpiCatalog, tpl.id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!title || !industry || !subIndustry) return;
    setSubmitting(true);
    const payload = {
      title,
      description: description || null,
      industry: Number(industry),
      sub_industry: Number(subIndustry),
      status,
      referenced_assessment_id: referencedAssessmentId ?? undefined,
      kpis: kpis.map((k, idx) => ({
        kpi_id: k.id,
        display_order: idx,
        is_mandatory: !!k.is_mandatory,
        weightage:
          k.weightage === null || k.weightage === undefined || k.weightage === ""
            ? undefined
            : Number(k.weightage),
        source: k.source || "company",
        source_kpi_id:
          k.source === "riu_backend" ? k.source_kpi_id ?? k.id : undefined,
        source_assessment_id:
          k.source === "riu_backend" ? k.source_assessment_id ?? undefined : undefined,
        parent_kpi_id: k.parent_kpi_id ?? undefined,
        parent_kpi_id_source:
          k.parent_kpi_id != null ? k.parent_kpi_id_source ?? "company" : undefined,
        parent_source_assessment_id:
          k.parent_kpi_id != null && k.parent_kpi_id_source === "riu_backend"
            ? k.parent_source_assessment_id ?? undefined
            : undefined,
      })),
    };
    const res = isEdit
      ? await AssessmentService.updateAssessment(assessmentId, payload, "company")
      : await AssessmentService.createAssessment(payload);
    setSubmitting(false);
    if (res?.isSuccess) {
      const newId = res?.data?.data?.id || assessmentId;
      const companyId = JSON.parse(localStorage.getItem("currentUser"))
        ?.company_id;
      history.push(
        newId ? `/assessment/${newId}` : `/assessment/active/${companyId}`
      );
    }
  };

  const renderKpiRow = (k, idx, isChild) => {
    const key = kpiKey(k);
    return (
      <tr key={key}>
        <td>{isChild ? "" : idx + 1}</td>
        <td style={{ paddingLeft: isChild ? 32 : undefined }}>
          {isChild ? "↳ " : ""}
          {k.title || "(untitled)"}
        </td>
        <td>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              padding: "2px 8px",
              borderRadius: 4,
              color: k.source === "company" ? "#065f46" : "#1e40af",
              background: k.source === "company" ? "#d1fae5" : "#dbeafe",
            }}
          >
            {k.source === "company" ? "MY KPI" : "RIU"}
          </span>
        </td>
        <td>
          <Form.Check
            type="checkbox"
            id={`mandatory-${key}`}
            checked={!!k.is_mandatory}
            onChange={() => toggleMandatory(key)}
            label="Mandatory"
          />
        </td>
        <td>
          <input
            type="number"
            min="0"
            step="0.01"
            value={k.weightage ?? ""}
            onChange={(e) => setWeightage(key, e.target.value)}
            placeholder={
              k.default_weightage != null
                ? String(k.default_weightage)
                : "—"
            }
            title={
              k.default_weightage != null
                ? `Default: ${k.default_weightage}. Blank = use default.`
                : "No default. Blank = no weightage."
            }
            style={{
              width: 80,
              height: 30,
              padding: "0 8px",
              border: "1px solid var(--rule)",
              borderRadius: 4,
              fontSize: 12,
            }}
          />
        </td>
        <td style={{ textAlign: "center" }}>
          {!isChild && (
            <button
              type="button"
              onClick={() => openSubPicker(k)}
              style={{
                background: "transparent",
                border: "1px solid var(--rule)",
                borderRadius: 4,
                padding: "2px 8px",
                fontSize: 11,
                cursor: "pointer",
                marginRight: 8,
              }}
              title="Add sub-KPI under this KPI"
            >
              + Sub
            </button>
          )}
          <span
            style={{ cursor: "pointer", color: "#b42318" }}
            onClick={() => removeKpi(key)}
            title="Remove"
          >
            <i className="fas fa-trash" />
          </span>
        </td>
      </tr>
    );
  };

  return (
    <SupplierManagementShell
      pathname={location.pathname}
      pageTitle={isEdit ? "Edit Assessment" : "Create Assessment"}
    >
      <Form onSubmit={handleSubmit}>
        <div style={cardSurface}>
          <div style={cardHeader}>
            <h5 style={cardHeaderTitle}>Assessment Details</h5>
            <div style={{ display: "flex", gap: 8 }}>
              {!isEdit && (
                <button
                  type="button"
                  style={secondaryBtn}
                  onClick={openTemplatePicker}
                >
                  Start from RIU template
                </button>
              )}
              <button
                type="button"
                style={secondaryBtn}
                onClick={() => history.goBack()}
              >
                Back
              </button>
            </div>
          </div>
          <div style={cardBody}>
            {referencedAssessmentId ? (
              <div
                style={{
                  padding: "8px 12px",
                  marginBottom: 12,
                  fontSize: 12,
                  background: "#eff6ff",
                  border: "1px solid #bfdbfe",
                  borderRadius: 6,
                  color: "#1e3a8a",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  Referenced RIU template:{" "}
                  <b>
                    {referencedAssessmentTitle
                      ? referencedAssessmentTitle
                      : `#${referencedAssessmentId}`}
                  </b>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    setReferencedAssessmentId(null);
                    setReferencedAssessmentTitle("");
                  }}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#1e3a8a",
                    cursor: "pointer",
                    fontSize: 12,
                    textDecoration: "underline",
                  }}
                >
                  Clear
                </button>
              </div>
            ) : null}
            <div style={{ marginBottom: 16 }}>
              <label style={formLabel}>Title *</label>
              <Form.Control
                required
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. BRSR Scope-3 baseline"
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={formLabel}>Description</label>
              <Form.Control
                as="textarea"
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div style={{ display: "flex", gap: 16, marginBottom: 16 }}>
              <div style={{ flex: 1 }}>
                <label style={formLabel}>Industry *</label>
                <select
                  className="kpi-input"
                  required
                  value={industry}
                  onChange={(e) => {
                    setIndustry(e.target.value);
                    setSubIndustry("");
                  }}
                  disabled={isEdit}
                  title={isEdit ? "Industry cannot be changed after creation" : undefined}
                  style={{
                    height: 38,
                    padding: "0 12px",
                    cursor: isEdit ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">Select industry…</option>
                  {industries.map((i) => (
                    <option key={i.id} value={i.id}>
                      {i.name}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ flex: 1 }}>
                <label style={formLabel}>Sub-industry *</label>
                <select
                  className="kpi-input"
                  required
                  disabled={isEdit || !industry}
                  title={isEdit ? "Sub-industry cannot be changed after creation" : undefined}
                  value={subIndustry}
                  onChange={(e) => setSubIndustry(e.target.value)}
                  style={{
                    height: 38,
                    padding: "0 12px",
                    cursor: isEdit ? "not-allowed" : "pointer",
                  }}
                >
                  <option value="">Select sub-industry…</option>
                  {subIndustries.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 4 }}>
              <label style={formLabel}>Status</label>
              <select
                className="kpi-input"
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                style={{ maxWidth: 220, height: 38, padding: "0 12px", cursor: "pointer" }}
              >
                <option value="DRAFT">DRAFT</option>
                <option value="PUBLISHED">PUBLISHED</option>
              </select>
            </div>
          </div>
        </div>

        <div style={{ ...cardSurface, marginTop: 18 }}>
          <div style={cardHeader}>
            <h5 style={cardHeaderTitle}>
              KPIs ({topLevel.length}
              {kpis.length > topLevel.length ? ` + ${kpis.length - topLevel.length} sub` : ""})
            </h5>
            <button type="button" style={secondaryBtn} onClick={openTopLevelPicker}>
              + {topLevel.length ? "Edit selection" : "Pick KPIs"}
            </button>
          </div>
          {topLevel.length === 0 ? (
            <div
              style={{
                padding: "32px 20px",
                textAlign: "center",
                color: "var(--ink-4)",
                fontSize: 13,
              }}
            >
              No KPIs selected yet. Click "Pick KPIs" to add some, or start from
              an RIU template above.
            </div>
          ) : (
            <div style={tableCardSurface}>
              <div className="supplier-mgmt-table-scroll">
                <table className="supplier-mgmt-table table">
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, width: 50 }}>#</th>
                      <th style={thStyle}>KPI</th>
                      <th style={{ ...thStyle, width: 100 }}>Source</th>
                      <th style={{ ...thStyle, width: 140 }}>Mandatory</th>
                      <th style={{ ...thStyle, width: 110 }}>Weightage</th>
                      <th style={{ ...thStyle, width: 130, textAlign: "center" }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {topLevel.flatMap((parent, idx) => {
                      const children =
                        childrenByParentKey.get(selfIdentity(parent)) || [];
                      return [
                        renderKpiRow(parent, idx, false),
                        ...children.map((c) => renderKpiRow(c, idx, true)),
                      ];
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 10,
            marginTop: 18,
          }}
        >
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => history.goBack()}
          >
            Cancel
          </button>
          <button type="submit" style={primaryBtn} disabled={submitting}>
            {submitting
              ? isEdit
                ? "Saving…"
                : "Creating…"
              : isEdit
              ? "Save Changes"
              : "Create Assessment"}
          </button>
        </div>
      </Form>

      <KpiPickerModal
        show={pickerOpen}
        onClose={closePicker}
        selectedKeys={pickerSelectedKeys}
        onConfirm={onPickerConfirm}
        title={subContext ? `Add sub-KPIs to "${subContext.title}"` : undefined}
      />

      <Modal
        show={templatePickerOpen}
        onHide={() => setTemplatePickerOpen(false)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton>
          <Modal.Title>Start from an RIU template</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: "60vh" }}>
          {loadingTemplates ? (
            <div className="text-center py-4 text-muted">Loading templates…</div>
          ) : templates.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No published RIU templates available.
            </div>
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Industry</th>
                  <th style={{ ...thStyle, width: 100 }}></th>
                </tr>
              </thead>
              <tbody>
                {templates.map((t) => (
                  <tr key={t.id}>
                    <td>{t.title}</td>
                    <td>{t.industry}</td>
                    <td>
                      <button
                        type="button"
                        style={primaryBtn}
                        onClick={() => chooseTemplate(t)}
                      >
                        Use
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            style={secondaryBtn}
            onClick={() => setTemplatePickerOpen(false)}
          >
            Cancel
          </button>
        </Modal.Footer>
      </Modal>
    </SupplierManagementShell>
  );
};

function kpiKey(k) {
  return `${k.source || "company"}:${k.id}`;
}

function selfIdentity(k) {
  const src = k.source || "company";
  const srcAss = src === "riu_backend" ? k.source_assessment_id ?? 0 : 0;
  return `${src}:${k.id}:${srcAss}`;
}

function parentIdentityFromChild(k) {
  if (k.parent_kpi_id == null) return null;
  const src = k.parent_kpi_id_source || "company";
  const srcAss = src === "riu_backend" ? k.parent_source_assessment_id ?? 0 : 0;
  return `${src}:${k.parent_kpi_id}:${srcAss}`;
}

function parentIdentityBaseKey(k) {
  return `${k.parent_kpi_id_source || "company"}:${k.parent_kpi_id}`;
}

/**
 * Convert a nested `kpis[]` tree from the server into a flat local state
 * array. Sub-KPIs get their parent metadata baked in. `templateOverride`
 * forces `source_assessment_id` on every row (used when composing from a
 * template).
 */
function flattenFromServerTree(nested, kpiCatalog, templateOverride) {
  const out = [];
  for (const parent of nested) {
    const pSrc = parent.source?.type || "company";
    const pKpiId =
      pSrc === "riu_backend"
        ? parent.source?.question_id ?? parent.kpi_id
        : parent.kpi_id;
    const pSourceAssessmentId =
      pSrc === "riu_backend"
        ? templateOverride ?? parent.source?.assessment_id ?? null
        : null;
    const parentSrcForTemplate = templateOverride ? "riu_backend" : pSrc;
    const parentSrcAssForTemplate = templateOverride
      ? templateOverride
      : pSourceAssessmentId;

    const parentRow = {
      id: pKpiId,
      title: parent.kpi_title || kpiCatalog[pKpiId] || "",
      source: templateOverride ? "riu_backend" : pSrc,
      source_kpi_id: templateOverride ? pKpiId : pSrc === "riu_backend" ? pKpiId : undefined,
      source_assessment_id: parentSrcAssForTemplate,
      is_mandatory: !!parent.is_mandatory,
      weightage:
        parent.weightage_override != null
          ? String(parent.weightage_override)
          : null,
      default_weightage: parent.default_weightage ?? null,
      parent_kpi_id: null,
      parent_kpi_id_source: null,
      parent_source_assessment_id: null,
    };
    out.push(parentRow);

    for (const child of parent.sub_kpis || []) {
      const cSrc = child.source?.type || "company";
      const cKpiId =
        cSrc === "riu_backend"
          ? child.source?.question_id ?? child.kpi_id
          : child.kpi_id;
      const cSourceAssessmentId =
        cSrc === "riu_backend"
          ? templateOverride ?? child.source?.assessment_id ?? null
          : null;
      if (
        cKpiId === parentRow.id &&
        (templateOverride ? "riu_backend" : cSrc) === parentRow.source
      ) {
        continue;
      }
      out.push({
        id: cKpiId,
        title: child.kpi_title || kpiCatalog[cKpiId] || "",
        source: templateOverride ? "riu_backend" : cSrc,
        source_kpi_id: templateOverride
          ? cKpiId
          : cSrc === "riu_backend"
          ? cKpiId
          : undefined,
        source_assessment_id: templateOverride
          ? templateOverride
          : cSourceAssessmentId,
        is_mandatory: !!child.is_mandatory,
        weightage:
          child.weightage_override != null
            ? String(child.weightage_override)
            : null,
        default_weightage: child.default_weightage ?? null,
        parent_kpi_id: parentRow.id,
        parent_kpi_id_source: parentRow.source,
        parent_source_assessment_id:
          parentRow.source === "riu_backend" ? parentRow.source_assessment_id ?? null : null,
      });
    }
  }
  return out;
}

export default CreateAssessment;
